<template>
  <div class="col-md-6 offset-md-3">
    <div class="login-form mt-4 p-4">
      <div class="row g-3">
        <h4>Регистрация в Хлебушке</h4>
        <div class="col-12">
          <label>Почта</label>
          <input type="email" name="email" class="form-control" placeholder="Email" v-model="email">
        </div>
        <div class="col-12">
          <label>Имя</label>
          <input type="text" name="name" class="form-control" placeholder="Name" v-model="name">
        </div>
        <div class="col-12">
          <label>Пароль</label>
          <input type="password" name="password" class="form-control" placeholder="Password" v-model="password">
        </div>
        <div class="col-12">
          <div class="form-check">
            <input
                class="form-check-input"
                type="checkbox"
                id="termsCheckbox"
                data-testid="terms-checkbox"
                v-model="termsAccepted"
            >
            <label class="form-check-label" for="termsCheckbox">
              Я согласен с
              <router-link to="/terms" target="_blank" class="text-decoration-none">условиями использования</router-link>
              и
              <router-link to="/privacy" target="_blank" class="text-decoration-none">политикой конфиденциальности</router-link>
            </label>
          </div>
        </div>
        <div class="col-12 error-placeholder">
          <error-label/>
        </div>
        <div class="col-12">
          <button type="submit" class="btn btn-dark float-end" @click="handleClickActionRegister" :disabled="disabledSubmit">Регистрация</button>
        </div>
      </div>
      <hr class="mt-4">
      <div class="col-12 mode-switcher-placeholder">
        <p class="text-center mb-0">Есть аккаунт? <a href="#" @click="handleClickModeRestore" data-mode="restore">Восстановить</a> <a href="#" @click="handleClickModeLogin" data-mode="login">Войти</a></p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, getCurrentInstance } from 'vue';
import { useStore } from 'vuex';
import ErrorLabel from "@/components/ui/ErrorLabel.vue";
import { latestMessage, type WebSocketMessageWithFields, type WebSocketMessageError } from "@/plugins/ws";
import { useToast } from "@/composables/useToast";
import {RootState} from "@/store";

defineOptions({
  name: 'ComponentsAuthRegister',
});

interface RegisterPayload {
  idUser: number;
  name: string;
  email: string;
  theme: string;
}

const emit = defineEmits<{
  switchMode: [mode: string];
}>();

const store = useStore<RootState>();

// Типы для WebSocket сообщений компонента Register
type RegisterSuccessMessage = WebSocketMessageWithFields<'auth-register', {
  idUser: number;
  name: string;
  email: string;
  theme: string;
}>;
type RegisterFailMessage = WebSocketMessageError<'auth-register-fail'>;

// Объединенный тип для всех сообщений
type RegisterWebSocketMessageType = RegisterSuccessMessage | RegisterFailMessage;

const internalInstance = getCurrentInstance();
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const wsSend = internalInstance?.appContext.config.globalProperties.$wsSend as (signal: string, payload: any) => void;

const email = ref('');
const name = ref('');
const password = ref('');
const termsAccepted = ref(false);
const latestMessageValue = ref(latestMessage);
const disabledSubmit = ref(false);

const theme = computed(() => store.state.theme.theme);

const setErrorReason = (reason: string) => store.dispatch('errorLabel/setErrorReason', reason);
const unsetErrorReason = () => store.dispatch('errorLabel/unsetErrorReason');

const { showToast } = useToast();

watch(email, () => {
  unsetErrorReason();
});

watch(name, () => {
  unsetErrorReason();
});

watch(password, () => {
  unsetErrorReason();
});

watch(termsAccepted, () => {
  unsetErrorReason();
});

// WebSocket message handler
const handleWebSocketMessage = (data: RegisterWebSocketMessageType): void => {
  if (!data?.signal) return;

  switch (data.signal) {
    case 'auth-register':
      signalRegister({
        idUser: data.idUser,
        name: data.name,
        email: data.email,
        theme: data.theme,
      });
      break;
    case 'auth-register-fail':
      signalRegisterFail(data.reason);
      break;
  }
};

watch(latestMessageValue, handleWebSocketMessage);

const handleClickActionRegister = () => {
  // Валидация checkbox
  if (!termsAccepted.value) {
    setErrorReason('Необходимо согласиться с условиями использования и политикой конфиденциальности');
    return;
  }

  // Очищаем ошибку если она была
  unsetErrorReason();

  wsSend('authRegister', {
    email: email.value,
    name: name.value,
    password: password.value,
    theme: theme.value,
  });
  disabledSubmit.value = true;
};

const handleClickModeRestore = () => {
  emit('switchMode', 'restore');
};

const handleClickModeLogin = () => {
  emit('switchMode', 'login');
};

const signalRegister = (payload: RegisterPayload) => {
  unsetErrorReason();
  disabledSubmit.value = false;
  store.dispatch('auth/wsSetUser', {
    idUser: payload.idUser,
    email: payload.email,
    name: payload.name,
    theme: payload.theme,
    admin: false,
    confirmEmail: false,
  });
  showToast('Регистрация успешна', 'Проверьте почту для подтверждения', 5000);
  emit('switchMode', 'registerCode');
};

const signalRegisterFail = (payload: string) => {
  setErrorReason(payload);
  disabledSubmit.value = false;
  showToast('Ошибка регистрации', payload, 15000);
};
</script>

<style scoped>

</style>